import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { http, trackEvent, gaEvent } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/lib/seo";
import { Crest } from "@/components/marketing/Wordmark";

export default function PaymentSuccess() {
  useSeo({ title: "Enrollment Confirmation | Infant Sleep Consultant Near Me Education Center", noindex: true });
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const sessionId = sp.get("session_id");
  const [status, setStatus] = useState("checking");
  const [info, setInfo] = useState(null);
  const [nextStep, setNextStep] = useState({ label: "Go to My Portal", to: "/portal" });

  const NEXT = {
    isc_diagnostic: { label: "Go to My Clarity", to: "/portal/clarity" },
    isc_blueprint: { label: "Review & Complete My Blueprint", to: "/portal/blueprint-intake" },
    isc_guided: { label: "Begin With Clarity", to: "/portal/diagnostic" },
    isc_guided_standalone: { label: "Set Up Guided Support", to: "/portal/guided" },
    isc_inhome_overnight: { label: "Begin My Onboarding", to: "/portal/inhome" },
    isc_is101_tuition: { label: "Begin IS-101", to: "/portal/resources" },
    isc_ns101_tuition: { label: "Begin NS-101", to: "/portal/resources?course=ns-101" },
    isc_is201_tuition: { label: "Begin IS-201", to: "/portal/resources?course=is-201" },
    isc_is301_tuition: { label: "Begin IS-301", to: "/portal/resources?course=is-301" },
    isc_is401_tuition: { label: "Begin IS-401", to: "/portal/resources?course=is-401" },
    ...Object.fromEntries(["is101", "ns101", "is201", "is301", "is401"].map((k) => [`isc_${k}_faculty`, { label: "View My Course & Meetings", to: "/portal#upcoming-classes" }])),
  };

  useEffect(() => {
    let attempts = 0;
    let timer;
    const poll = async () => {
      if (!sessionId) { setStatus("error"); return; }
      try {
        const { data } = await http.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "refunded" && ["refunded_duplicate", "refunded_cohort_canceled"].includes(data.status)) {
          const canceledClass = data.status === "refunded_cohort_canceled";
          setNextStep({ label: canceledClass ? "Choose Another Class Date" : "Go to My Portal", to: canceledClass ? "/courses" : "/portal",
            note: canceledClass ? "The class you selected was canceled before your payment completed, so your payment has been refunded in full. You can choose another class date or Independent Study."
              : "You were already enrolled, so this extra payment has been refunded automatically." });
          setStatus("paid"); return;
        }
        if (data.payment_status === "paid") {
          const step = NEXT[data.lookup_key] || { label: "Go to My Portal", to: "/portal" };
          setNextStep({ ...step, duplicate: data.status === "refunded_duplicate" });
          setInfo(data.course ? data : null);
          setStatus("paid");
          await refresh();
          // GA4 conversion — server is the source of truth and dedupes; fire once per transaction.
          try {
            const { data: ga } = await http.get(`/payments/ga-conversion/${sessionId}`);
            if (ga && ga.fire) {
              gaEvent("purchase", ga.params, ga.debug_mode);
              if (ga.product_event) gaEvent(ga.product_event, ga.params, ga.debug_mode);
              if (ga.params?.course_code) gaEvent("course_enrollment", ga.params, ga.debug_mode);
              // Google Ads purchase conversion: verified live course tuition only (no test mode, no consulting/booking products).
              if (ga.params?.course_code && !ga.debug_mode && window.gtag) {
                window.gtag("event", "conversion", { send_to: "AW-18426106228/581uCMq6jqsdEPSioNJE", value: ga.params.value, currency: "USD", transaction_id: ga.params.transaction_id });
              }
            }
            if (ga && ga.fire) trackEvent("checkout_success", { product_id: ga.params?.product_id || "", currency: "USD" });
          } catch (e) {}
          try { localStorage.removeItem("isc_pending_product"); } catch (e) {}
          if (!data.course) timer = setTimeout(() => nav(step.to), 2200);
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) {
          trackEvent("checkout_failed", { reason: data.payment_status });
          setStatus("error"); return;
        }
      } catch (e) {}
      if (attempts++ < 8) timer = setTimeout(poll, 1800);
      else setStatus("timeout");
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (info && status === "paid" && !nextStep.note && !nextStep.duplicate) return <Confirmed d={info} nav={nav} />;
  if (status === "checking" && sessionId) return (
    <div className="min-h-screen bg-[#172A3A] grid place-items-center px-5 text-center text-[#F5F2E9]" data-testid="enroll-finalizing">
      <div><Loader2 className="animate-spin mx-auto text-[#BDA677]" size={40} /><h1 className="font-academic text-3xl mt-5">Finalizing Your Enrollment.</h1>
        <p className="text-[#B9C0C9] mt-2 text-sm">We're confirming your payment with Stripe. This usually takes a few seconds.</p></div>
    </div>
  );
  return (
    <div className="min-h-screen bg-[#F9F8F6] grid place-items-center px-5">
      <div className="max-w-md w-full bg-white border border-[#E5E1D8] rounded-2xl p-10 text-center shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
        {status === "checking" && (<><Loader2 className="animate-spin mx-auto text-[#9F634E]" size={40} /><h1 className="font-display text-2xl mt-5 text-[#1A1918]">Confirming your payment…</h1><p className="text-[#6B665E] mt-2 text-sm">Just a moment.</p></>)}
        {status === "paid" && (<>
          <CheckCircle2 className="mx-auto text-[#667A66]" size={48} />
          <h1 data-testid="payment-success" className="font-display text-3xl mt-5 text-[#1A1918]">{nextStep.note ? "Payment refunded" : "You're all set."}</h1>
          <p className="text-[#6B665E] mt-3 text-[15px] leading-relaxed">{nextStep.note || (nextStep.duplicate ? "You were already enrolled, so this extra payment has been refunded automatically." : "Taking you to your next step now — your progress is always saved.")}</p>
          <button data-testid="go-to-portal" onClick={() => nav(nextStep.to)} className="mt-7 w-full py-3 rounded-full bg-[#9F634E] hover:bg-[#8C5441] text-white text-sm transition-colors">{nextStep.label}</button>
        </>)}
        {["error", "timeout"].includes(status) && (<>
          <XCircle className="mx-auto text-[#9E4F4F]" size={44} />
          <h1 className="font-display text-2xl mt-5 text-[#1A1918]">{status === "timeout" ? "Still processing" : "Something went wrong"}</h1>
          <p className="text-[#6B665E] mt-2 text-sm">{status === "timeout" ? "Your payment is still being confirmed by Stripe. Your course opens automatically once it's verified; check your dashboard shortly. You won't be charged twice." : "This payment was not completed, so no course access was granted. You can return to the course page and enroll again."}</p>
          <Link to="/portal" className="mt-6 inline-block w-full py-3 rounded-full bg-[#9F634E] text-white text-sm">Go to My Portal</Link>
        </>)}
      </div>
    </div>
  );
}

function Confirmed({ d, nav }) {
  const fl = d.format === "faculty_led";
  const r = d.receipt || {};
  const money = (n) => `$${Number(n || 0).toFixed(2)}`;
  return (
    <div className="min-h-screen bg-[#172A3A] px-5 py-14" data-testid="enroll-confirmed">
      <div className="max-w-2xl mx-auto bg-[#F5F2E9] text-[#172A3A] p-8 sm:p-12 border-t-4 border-[#BDA677]">
        <Crest size={40} />
        <h1 className="font-academic text-3xl sm:text-4xl font-semibold mt-5 leading-tight">Welcome to the Infant Sleep Consultant Near Me Education Center!</h1>
        <p className="mt-3 text-lg flex items-center gap-2" data-testid="payment-success"><CheckCircle2 className="text-[#56705B]" size={22} /> Your enrollment is confirmed.</p>
        <dl className="mt-6 grid sm:grid-cols-2 gap-4 text-sm border-y border-[#172A3A]/15 py-5">
          <div><dt className="text-xs uppercase tracking-[0.12em] text-[#647080]">Course</dt><dd className="mt-1 font-medium" data-testid="confirm-course">{d.course_code} {d.course_title}</dd></div>
          <div><dt className="text-xs uppercase tracking-[0.12em] text-[#647080]">Learning format</dt><dd className="mt-1" data-testid="confirm-format">{fl ? "Faculty-Led Study" : "Independent Study"}</dd></div>
          <div><dt className="text-xs uppercase tracking-[0.12em] text-[#647080]">Amount paid</dt><dd className="mt-1" data-testid="confirm-amount">{money(r.amount_total ?? d.tuition)}{r.tax ? ` (includes ${money(r.tax)} tax)` : ""}</dd></div>
          <div><dt className="text-xs uppercase tracking-[0.12em] text-[#647080]">Receipt</dt><dd className="mt-1">{r.receipt_url ? <a href={r.receipt_url} target="_blank" rel="noopener noreferrer" className="underline text-[#7A6438]" data-testid="confirm-receipt">View Stripe receipt</a> : "Emailed to you by Stripe"}</dd></div>
        </dl>
        <p className="text-sm mt-5" data-testid="confirm-access">Your complete {d.course_code} course is now open in your student dashboard.</p>
        {fl && <p className="text-sm mt-2" data-testid="confirm-faculty">Andrea Hurley will personally coordinate your four weekly 30-minute meetings. {d.calendly_url ? "You can book your first meeting now from your dashboard." : "She'll contact you shortly."}</p>}
        <div className="flex flex-col sm:flex-row gap-3 mt-7">
          <button onClick={() => nav("/portal")} className="btn-clay px-6 py-3.5" data-testid="go-to-portal">Go to My Student Dashboard</button>
          <button onClick={() => nav(`/portal/resources?course=${d.course}`)} className="border border-[#172A3A] px-6 py-3.5 text-sm" data-testid="confirm-begin">Begin My Course</button>
        </div>
      </div>
    </div>
  );
}
